import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import './styles/global.css';
import './App.css';
import Dashboard from './components/Dashboard';
import QuestionList from './components/QuestionList';
import QuizTaker from './components/QuizTaker';
import QuizTakerContainer from './components/quiz/QuizTakerContainer';
import StudyBank from './components/StudyBank';
import SkillSheetsList from './components/SkillSheetsList';
import SkillSheetDetail from './components/SkillSheetDetail';
import HamburgerMenu, { MenuOption } from './components/HamburgerMenu';
import { QuestionCounter } from './utils/questionCounters';
import { getBookData } from './types/utils';
import { AppBook } from './types/types';
import { analyticsCollector } from './utils/analytics';
import { allBooks } from './types/books';
import FlipChartPage from './components/FlipChartPage';

// Route tracking component for analytics
const RouteTracker: React.FC = () => {
    const location = useLocation();
    
    useEffect(() => {
        // Track page view whenever route changes
        console.log('RouteTracker: Route changed to:', location.pathname);
        analyticsCollector.trackPageView(location.pathname);
        console.log('RouteTracker: Page view tracked for:', location.pathname);
    }, [location.pathname]);
    
    console.log('RouteTracker: Component rendered, current path:', location.pathname);
    return null; // This component doesn't render anything
};

// Wrapper component to handle chapter routing
const QuizTakerWrapper: React.FC<{
    book: AppBook;
    selectedBook: string;
    selectedChapter: string;
    availableBooks: string[];
    availableChapters: string[];
    onBookChange: (bookId: string) => void;
    onChapterChange: (chapterId: string) => void;
}> = (props) => {
    const { bookId, chapterId } = useParams<{ bookId: string; chapterId: string }>();
    const { selectedChapter, onChapterChange } = props;
    
    // Load the correct book based on URL parameter
    const [currentBook, setCurrentBook] = useState<AppBook | null>(null);
    
    useEffect(() => {
        if (bookId) {
            console.log(`QuizTakerWrapper: Looking for book with ID: ${bookId}`);
            const book = allBooks.getBookById(bookId);
            if (book) {
                console.log(`QuizTakerWrapper: Found book: ${book.title}`);
                setCurrentBook(book);
            } else {
                console.log(`QuizTakerWrapper: Book not found for ID: ${bookId}`);
            }
        }
    }, [bookId]);
    
    // Update the selected chapter based on the URL parameter
    useEffect(() => {
        if (chapterId && chapterId !== selectedChapter) {
            onChapterChange(chapterId);
        }
    }, [chapterId, selectedChapter, onChapterChange]);

    // Use the book from URL if available, otherwise fall back to the prop
    const bookToUse = currentBook || props.book;

    // If chapterId is "all", show the quiz configuration interface
    if (chapterId === "all") {
        return (
            <QuizTakerContainer
                book={bookToUse}
                selectedBook={props.selectedBook}
                selectedChapter={props.selectedChapter}
                availableBooks={props.availableBooks}
                availableChapters={props.availableChapters}
                onBookChange={props.onBookChange}
                onChapterChange={props.onChapterChange}
                defaultQuestionType="true_false"
            />
        );
    }

    return (
        <QuizTaker
            book={bookToUse}
            selectedBook={props.selectedBook}
            selectedChapter={chapterId || props.selectedChapter}
            availableBooks={props.availableBooks}
            availableChapters={props.availableChapters}
            onBookChange={props.onBookChange}
            onChapterChange={props.onChapterChange}
            defaultQuestionType="true_false"
        />
    );
};


function App() {
    const [book, setBook] = useState<AppBook | null>(null);
    const loading = false; // Loading is never set to true in this component
    const [selectedBook, setSelectedBook] = useState<string>('9780879397241');
    const [selectedChapter, setSelectedChapter] = useState<string>('all');
    const availableBooks = ['9780879397241']; // Static list since setAvailableBooks is never used
    const [availableChapters, setAvailableChapters] = useState<string[]>([]);

    useEffect(() => {
        try {
            // Load book data with chapter structure
            const bookData = getBookData();
            const chapters = Object.keys(bookData.chapters);

            setAvailableChapters(chapters);
            setBook(bookData);
        } catch (error) {
            console.error('Error loading questions:', error);
        }
    }, [selectedBook]); // Remove selectedChapter dependency

    const handleMenuOptionSelect = (option: MenuOption) => {
        // This function is no longer needed as navigation is handled by routes
    };

    const handleBookChange = (bookId: string) => {
        setSelectedBook(bookId);
        setSelectedChapter('all'); // Reset chapter selection when book changes
    };

    const handleChapterChange = (chapterId: string) => {
        setSelectedChapter(chapterId);
    };

    if (loading || !book) {
        return (
            <div className="App">
                <div className="loading">
                    <h2>Loading Firefighting Questions...</h2>
                    <div className="spinner"></div>
                </div>
            </div>
        );
    }

    return (
        <Router>
            <RouteTracker />
            <div className="App">
                <HamburgerMenu
                    onOptionSelect={handleMenuOptionSelect}
                    trueFalseCount={book ? QuestionCounter.getTotalBookCounts(book).trueFalse : 0}
                    multipleChoiceCount={book ? QuestionCounter.getTotalBookCounts(book).multipleChoice : 0}
                />

                <main>
                    <Routes>
                        {/* Home Dashboard */}
                        <Route 
                            path="/" 
                            element={
                                <Dashboard
                                    book={book}
                                    selectedBook={selectedBook}
                                    selectedChapter={selectedChapter}
                                    availableBooks={availableBooks}
                                    availableChapters={availableChapters}
                                    onBookChange={handleBookChange}
                                    onChapterChange={handleChapterChange}
                                />
                            } 
                        />
                        
                        {/* Quiz Configuration */}
                        <Route 
                            path="/books/:bookId/quiz/:chapterId" 
                            element={
                                <QuizTakerWrapper
                                    book={book}
                                    selectedBook={selectedBook}
                                    selectedChapter={selectedChapter}
                                    availableBooks={availableBooks}
                                    availableChapters={availableChapters}
                                    onBookChange={handleBookChange}
                                    onChapterChange={handleChapterChange}
                                />
                            } 
                        />
                        
                        {/* Study Bank */}
                        <Route 
                            path="/study-bank" 
                            element={
                                <StudyBank
                                    book={book}
                                    selectedBook={selectedBook}
                                    selectedChapter={selectedChapter}
                                    availableBooks={availableBooks}
                                    availableChapters={availableChapters}
                                    onBookChange={handleBookChange}
                                    onChapterChange={handleChapterChange}
                                />
                            } 
                        />
                        
                        {/* Question Browser */}
                        <Route 
                            path="/books/:bookId/questions" 
                            element={
                                <QuestionList
                                    book={book}
                                    defaultQuestionType="true_false"
                                    selectedBook={selectedBook}
                                    selectedChapter={selectedChapter}
                                    availableBooks={availableBooks}
                                    availableChapters={availableChapters}
                                    onBookChange={handleBookChange}
                                    onChapterChange={handleChapterChange}
                                />
                            } 
                        />
                        
                        {/* Skill Sheets List */}
                        <Route 
                            path="/skill-sheets" 
                            element={<SkillSheetsList />} 
                        />
                        
                        {/* Skill Sheet Detail */}
                        <Route 
                            path="/skill-sheets/:id" 
                            element={<SkillSheetDetail />} 
                        />
                        
                        {/* Flip Chart Page */}
                        <Route 
                            path="/skill-sheets/:id/flip-chart" 
                            element={<FlipChartPage />} 
                        />
                        
                        
                        {/* Catch all other routes and redirect to home */}
                        <Route 
                            path="*" 
                            element={<Navigate to="/" replace />} 
                        />
                    </Routes>
                </main>
            </div>
        </Router>
    );
}

export default App;
